import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  message,
  Typography,
  Row,
  Col,
  Tooltip,
  Empty
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import MapPicker from '../../components/mapPicker';
import {
  fetchPlaces,
  createPlace,
  updatePlace,
  deletePlace,
  clearError} from '../../store/slices/placeSlice';

const { Title, Text } = Typography;

const Locations = () => {
  const dispatch = useDispatch();
  const { places, loading, error, status } = useSelector((state) => state.place);
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [editingPlace, setEditingPlace] = useState(null);
  const [viewingPlace, setViewingPlace] = useState(null);
  const [form] = Form.useForm();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [actionType, setActionType] = useState(null); // 'create', 'update', 'delete'

  useEffect(() => {
    dispatch(fetchPlaces());
  }, [dispatch]);

  useEffect(() => {
    if (status === 'failed' && error) {
      message.error(error);
      setActionType(null);
      dispatch(clearError());
    }
  }, [status, error, dispatch]);

  const handleCreate = () => {
    setEditingPlace(null);
    setSelectedLocation(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingPlace(record);
    setSelectedLocation({
      lat: record.latitude,
      lng: record.longitude
    });
    form.setFieldsValue({
      name: record.name,
      address: record.address,
      phone: record.phone,
      latitude: record.latitude,
      longitude: record.longitude
    });
    setIsModalVisible(true);
  };

  const handleView = (record) => {
    setViewingPlace(record);
    setIsViewModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      setActionType('delete');
      await dispatch(deletePlace(id)).unwrap();
      message.success('Konum başarıyla silindi');
      setActionType(null);
    } catch (error) {
      message.error('Konum silinirken bir hata oluştu');
      setActionType(null);
    }
  };

  const handleSubmit = async (values) => {
    const placeData = {
      name: values.name,
      address: values.address,
      phone: values.phone,
      latitude: selectedLocation?.lat || values.latitude,
      longitude: selectedLocation?.lng || values.longitude
    };

    try {
      if (editingPlace) {
        setActionType('update');
        await dispatch(updatePlace({ id: editingPlace.id, placeData })).unwrap();
        message.success('Konum başarıyla güncellendi');
        setIsModalVisible(false);
        form.resetFields();
        setEditingPlace(null);
        setSelectedLocation(null);
      } else {
        setActionType('create');
        await dispatch(createPlace(placeData)).unwrap();
        message.success('Konum başarıyla oluşturuldu');
        setIsModalVisible(false);
        form.resetFields();
        setEditingPlace(null);
        setSelectedLocation(null);
      }
      setActionType(null);
    } catch (error) {
      message.error('İşlem sırasında bir hata oluştu');
      setActionType(null);
    }
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    form.setFieldsValue({
      latitude: location.lat,
      longitude: location.lng
    });
  };

  const columns = [
    {
      title: 'Konum Adı',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Adres',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => (
        <Space>
          <PhoneOutlined />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Koordinatlar',
      key: 'coordinates',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Enlem: {record.latitude?.toFixed(6)}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Boylam: {record.longitude?.toFixed(6)}
          </Text>
        </Space>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Görüntüle">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Düzenle">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Bu Konumu silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Tooltip title="Sil">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Title level={2} style={{ margin: 0 }}>
            <EnvironmentOutlined style={{ marginRight: '8px' }} />
            Konumlar
          </Title>
          <Text type="secondary">
            Spor okulu Konumlarını yönetin
          </Text>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong>Konum Listesi</Text>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            loading={loading}
          >
            Yeni Konum Ekle
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={places}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} Konum`,
          }}
          locale={{
            emptyText: (
              <Empty
                description="Henüz Konum eklenmemiş"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingPlace ? 'Konum Düzenle' : 'Yeni Konum Ekle'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingPlace(null);
          setSelectedLocation(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Konum Adı"
                rules={[{ required: true, message: 'Lütfen Konum adını girin' }]}
              >
                <Input placeholder="Konum adını girin" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="address"
                label="Adres"
                rules={[{ required: true, message: 'Lütfen adresi girin' }]}
              >
                <Input placeholder="Adresi girin" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Telefon"
                rules={[
                  { required: true, message: 'Lütfen telefon numarasını girin' },
                  { pattern: /^[0-9+\-\s()]+$/, message: 'Geçerli bir telefon numarası girin' }
                ]}
              >
                <Input placeholder="Telefon numarasını girin" prefix={<PhoneOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="latitude"
                label="Enlem"
                rules={[{ required: true, message: 'Lütfen enlem değerini girin' }]}
              >
                <Input placeholder="Enlem değeri" type="number" step="any" disabled={true} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="longitude"
                label="Boylam"
                rules={[{ required: true, message: 'Lütfen boylam değerini girin' }]}
              >
                <Input placeholder="Boylam değeri" type="number" step="any" disabled={true} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Haritadan Konum Seç">
            <MapPicker
              onSelect={handleLocationSelect}
              initialLat={selectedLocation?.lat || 39.9334}
              initialLng={selectedLocation?.lng || 32.8597}
              showInitialMarker={!!selectedLocation}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setEditingPlace(null);
                  setSelectedLocation(null);
                  form.resetFields();
                }}
              >
                İptal
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingPlace ? 'Güncelle' : 'Oluştur'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Modal */}
      <Modal
        title="Konum Detayları"
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            Kapat
          </Button>
        ]}
        width={600}
      >
        {viewingPlace && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Text strong>Konum Adı:</Text>
                <br />
                <Text>{viewingPlace.name}</Text>
              </Col>
              <Col span={24}>
                <Text strong>Adres:</Text>
                <br />
                <Text>{viewingPlace.address}</Text>
              </Col>
              <Col span={24}>
                <Text strong>Telefon:</Text>
                <br />
                <Space>
                  <PhoneOutlined />
                  <Text>{viewingPlace.phone}</Text>
                </Space>
              </Col>
              <Col span={12}>
                <Text strong>Enlem:</Text>
                <br />
                <Text>{viewingPlace.latitude?.toFixed(6)}</Text>
              </Col>
              <Col span={12}>
                <Text strong>Boylam:</Text>
                <br />
                <Text>{viewingPlace.longitude?.toFixed(6)}</Text>
              </Col>
            </Row>
            
            <div style={{ marginTop: '16px' }}>
              <Text strong>Konum:</Text>
              <div style={{ marginTop: '8px' }}>
                <MapPicker
                  onSelect={() => {}}
                  initialLat={viewingPlace.latitude}
                  initialLng={viewingPlace.longitude}
                  showInitialMarker={true}
                  isView={true}
                />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Locations;